import { useQuery } from '@apollo/client';
import { Alert, Pill, Spinner, Stack } from '@sixfold/common-ui';
import { isNil, notNil } from '@sixfold/typed-primitives';
import { gql } from 'graphql-tag';
import React, { useMemo } from 'react';
import { useLocation } from 'react-router';

import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { useTimeZone } from '../../components/date_formatting/timezone_context';
import {
  TourFilteringTransportMode,
  TourVehicleHistoryQuery,
  TourVehicleHistoryQueryVariables,
  TransportMode,
} from '../../lib/graphql';
import { Routes } from '../../routes';
import { Tour, TrackingSegment } from '../entities';
import { vehicleHistoryQuery } from '../graphql';
import { extractVehicleHistoryVariables } from '../utils';

interface TourAnalyticsMetrics {
  is_tracked: boolean | null;
  is_allocated: boolean | null;
  has_telemetry: boolean | null;
  stop_count: number | null;
  stops_with_eta_count: number | null;
  stops_with_external_eta_count: number | null;
  real_time_stop_arrivals_count: number | null;
  has_all_stops_arrived_real_time: boolean | null;
  allocation_timeliness_percentage: number | null;
  allocation_late_minutes: number | null;
  mean_telemetry_delay_minutes: number | null;
  external_arrival_event_average_delay_minutes: number | null;
  has_arrivals_to_all_stops: boolean | null;
  visited_stop_count: number | null;
  telemetry_visited_stop_count: number | null;
  event_visited_stop_count: number | null;
  smart_break_visited_stop_count: number | null;
  has_eta_to_all_stops: boolean | null;
  eta_count: number | null;
  external_eta_count: number | null;
  moving_duration_hours: number | null;
  moving_pings_count: number | null;
  moving_pings_per_hour: number | null;
  tour_segments_count: number | null;
  tour_segments_ping_count: number | null;
  telemetry_consistency_percentage: number | null;
}

interface HistoryPing {
  timestamp: string;
  received_at: string | null;
  created_at: string | null;
}

interface VehicleBreakItem {
  from: string;
  to: string | null;
}

interface PingStats {
  totalPings: number;
  averageIntervalMinutes: number | null;
  meanIngestionDelaySeconds: number | null;
  meanProcessingTimeSeconds: number | null;
  medianProcessingTimeSeconds: number | null;
  minTimestamp: string | null;
  maxTimestamp: string | null;
  minReceivedAt: string | null;
  maxReceivedAt: string | null;
}

interface VehiclePingsRow {
  vehicleId: string;
  licensePlate: string | null;
  all: PingStats;
  moving: PingStats;
}

interface TourAnalyticsMetricsQuery {
  company: {
    tourVisibilityPerformance: {
      tourMetrics: {
        edges: Array<{ node: TourAnalyticsMetrics | null } | null> | null;
      };
    } | null;
  } | null;
}

interface TourAnalyticsMetricsQueryVariables {
  company_id: string;
  tour_id: string;
  transportMode?: TourFilteringTransportMode[];
}

const tourAnalyticsMetricsQuery = gql`
  query TourAnalyticsMetrics($company_id: String!, $tour_id: String!, $transportMode: [TourFilteringTransportMode!]) {
    company(company_id: $company_id) {
      tourVisibilityPerformance(
        viewMode: RECEIVING_VISIBILITY
        filter: { tourIds: [$tour_id], transportMode: $transportMode }
      ) {
        tourMetrics(first: 1) {
          edges {
            node {
              is_tracked
              is_allocated
              has_telemetry
              stop_count
              stops_with_eta_count
              stops_with_external_eta_count
              real_time_stop_arrivals_count
              has_all_stops_arrived_real_time
              allocation_timeliness_percentage
              allocation_late_minutes
              mean_telemetry_delay_minutes
              external_arrival_event_average_delay_minutes
              has_arrivals_to_all_stops
              visited_stop_count
              telemetry_visited_stop_count
              event_visited_stop_count
              smart_break_visited_stop_count
              has_eta_to_all_stops
              eta_count
              external_eta_count
              moving_duration_hours
              moving_pings_count
              moving_pings_per_hour
              tour_segments_count
              tour_segments_ping_count
              telemetry_consistency_percentage
            }
          }
        }
      }
    }
  }
`;

const DASH = '—';

const withDash =
  <T,>(render: (value: T) => React.ReactNode) =>
  (value: T | null | undefined): React.ReactNode =>
    isNil(value) ? DASH : render(value);

const formatBool = withDash<boolean>((v) => (v ? <Pill kind="success">Yes</Pill> : <Pill kind="danger">No</Pill>));
const formatNumber = withDash<number>((v) =>
  v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
);
const formatPercentage = withDash<number>((v) => `${v.toFixed(1)}%`);
const formatMinutes = withDash<number>((v) => `${v.toFixed(1)} min`);
const formatHours = withDash<number>((v) => `${v.toFixed(1)} h`);
const formatPerHour = withDash<number>((v) => `${v.toFixed(1)} / h`);
const formatIsoDate = withDash<string>((iso) => <FormattedDate date={iso} showTimezone={false} />);

/**
 * Pick a human-friendly unit for a duration in seconds.
 * Sub-second → ms, sub-minute → s, sub-hour → min, sub-day → h+min, else d+h.
 */
const formatDurationSeconds = withDash<number>((seconds) => {
  const absSec = Math.abs(seconds);
  const sign = seconds < 0 ? '-' : '';

  if (absSec < 1) return `${sign}${(absSec * 1000).toFixed(0)} ms`;
  if (absSec < 60) return `${sign}${absSec.toFixed(1)} s`;
  if (absSec < 3_600) return `${sign}${(absSec / 60).toFixed(1)} min`;
  if (absSec < 86_400) {
    const h = Math.floor(absSec / 3_600);
    const m = Math.round((absSec % 3_600) / 60);
    return `${sign}${h}h ${m}min`;
  }
  const d = Math.floor(absSec / 86_400);
  const h = Math.round((absSec % 86_400) / 3_600);
  return `${sign}${d}d ${h}h`;
});

const parseValidDate = (input: string | null | undefined): number | null => {
  if (isNil(input)) return null;
  const t = new Date(input).getTime();
  return Number.isNaN(t) ? null : t;
};

const computeMean = (values: number[]): number | null =>
  values.length === 0 ? null : values.reduce((acc, v) => acc + v, 0) / values.length;

const computeMedian = (values: number[]): number | null => {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
};

const isoRangeOfSorted = (sortedTimes: number[]): { min: string | null; max: string | null } => {
  if (sortedTimes.length === 0) return { min: null, max: null };
  return {
    min: new Date(sortedTimes[0]).toISOString(),
    max: new Date(sortedTimes[sortedTimes.length - 1]).toISOString(),
  };
};

const EMPTY_PING_STATS: PingStats = {
  totalPings: 0,
  averageIntervalMinutes: null,
  meanIngestionDelaySeconds: null,
  meanProcessingTimeSeconds: null,
  medianProcessingTimeSeconds: null,
  minTimestamp: null,
  maxTimestamp: null,
  minReceivedAt: null,
  maxReceivedAt: null,
};

const computePingStats = (pings: ReadonlyArray<HistoryPing>): PingStats => {
  if (pings.length === 0) return EMPTY_PING_STATS;

  const timestamps: number[] = [];
  const receivedAts: number[] = [];
  const ingestionDelaysSeconds: number[] = [];
  const processingTimesSeconds: number[] = [];

  for (const p of pings) {
    const ts = parseValidDate(p.timestamp);
    const rec = parseValidDate(p.received_at);
    const proc = parseValidDate(p.created_at);

    if (ts !== null) timestamps.push(ts);
    if (rec !== null) receivedAts.push(rec);
    if (ts !== null && rec !== null) ingestionDelaysSeconds.push((rec - ts) / 1000);
    if (rec !== null && proc !== null) processingTimesSeconds.push((proc - rec) / 1000);
  }

  timestamps.sort((a, b) => a - b);
  receivedAts.sort((a, b) => a - b);

  const tsRange = isoRangeOfSorted(timestamps);
  const recRange = isoRangeOfSorted(receivedAts);

  const averageIntervalMinutes =
    timestamps.length >= 2
      ? (timestamps[timestamps.length - 1] - timestamps[0]) / (timestamps.length - 1) / 60_000
      : null;

  return {
    totalPings: pings.length,
    averageIntervalMinutes,
    meanIngestionDelaySeconds: computeMean(ingestionDelaysSeconds),
    meanProcessingTimeSeconds: computeMean(processingTimesSeconds),
    medianProcessingTimeSeconds: computeMedian(processingTimesSeconds),
    minTimestamp: tsRange.min,
    maxTimestamp: tsRange.max,
    minReceivedAt: recRange.min,
    maxReceivedAt: recRange.max,
  };
};

/**
 * "Moving" pings mirror tour-aggregator-service `calculateTelemetryMetrics`:
 *  - timestamp must fall inside one of the tour's tracking segments for this vehicle (inclusive),
 *  - timestamp must NOT fall strictly inside any break (exclusive boundaries, matching
 *    `excludeBreakDurationFromHistory(..., excludeBreakBoundaries=false)`).
 */
const filterMovingPings = (
  pings: ReadonlyArray<HistoryPing>,
  trackingSegments: ReadonlyArray<TrackingSegment>,
  breaks: ReadonlyArray<VehicleBreakItem>,
): HistoryPing[] => {
  if (trackingSegments.length === 0) return [];

  const segmentRanges = trackingSegments
    .map((s) => ({
      start: new Date(s.trackingStartDate).getTime(),
      end: new Date(s.trackingEndDate).getTime(),
    }))
    .filter((r) => !Number.isNaN(r.start) && !Number.isNaN(r.end));

  const breakRanges = breaks
    .map((b) => ({
      start: new Date(b.from).getTime(),
      end: b.to !== null ? new Date(b.to).getTime() : Number.POSITIVE_INFINITY,
    }))
    .filter((r) => !Number.isNaN(r.start));

  return pings.filter((p) => {
    const t = parseValidDate(p.timestamp);
    if (t === null) return false;
    const inSegment = segmentRanges.some((r) => t >= r.start && t <= r.end);
    if (!inSegment) return false;
    const inBreak = breakRanges.some((r) => t > r.start && t < r.end);
    return !inBreak;
  });
};

type MetricRow = { label: string; value: React.ReactNode };
type MetricGroup = { title: string; rows: MetricRow[] };

const buildMetricGroups = (m: TourAnalyticsMetrics): MetricGroup[] => [
  {
    title: 'Tracking',
    rows: [
      { label: 'Tracked', value: formatBool(m.is_tracked) },
      { label: 'Allocated', value: formatBool(m.is_allocated) },
      { label: 'Has telemetry', value: formatBool(m.has_telemetry) },
    ],
  },
  {
    title: 'Allocation',
    rows: [
      { label: 'Allocation timeliness', value: formatPercentage(m.allocation_timeliness_percentage) },
      { label: 'Allocation late', value: formatMinutes(m.allocation_late_minutes) },
    ],
  },
  {
    title: 'Stop visits',
    rows: [
      { label: 'Stop count', value: formatNumber(m.stop_count) },
      { label: 'Visited stops', value: formatNumber(m.visited_stop_count) },
      { label: 'All stops visited', value: formatBool(m.has_arrivals_to_all_stops) },
      { label: 'Telemetry-visited stops', value: formatNumber(m.telemetry_visited_stop_count) },
      { label: 'Event-visited stops', value: formatNumber(m.event_visited_stop_count) },
      { label: 'Smart-detection-visited stops', value: formatNumber(m.smart_break_visited_stop_count) },
      { label: 'Real-time stop arrivals', value: formatNumber(m.real_time_stop_arrivals_count) },
      { label: 'All stops arrived in real time', value: formatBool(m.has_all_stops_arrived_real_time) },
    ],
  },
  {
    title: 'GPS quality',
    rows: [
      { label: 'Moving duration', value: formatHours(m.moving_duration_hours) },
      { label: 'Moving pings', value: formatNumber(m.moving_pings_count) },
      { label: 'Moving pings per hour', value: formatPerHour(m.moving_pings_per_hour) },
      { label: 'Tour segments', value: formatNumber(m.tour_segments_count) },
      { label: 'Tour segments ping count', value: formatNumber(m.tour_segments_ping_count) },
      { label: 'Telemetry consistency', value: formatPercentage(m.telemetry_consistency_percentage) },
      { label: 'Mean telemetry delay', value: formatMinutes(m.mean_telemetry_delay_minutes) },
      {
        label: 'External arrival event avg. delay',
        value: formatMinutes(m.external_arrival_event_average_delay_minutes),
      },
    ],
  },
  {
    title: 'ETA presence',
    rows: [
      { label: 'ETA for all stops', value: formatBool(m.has_eta_to_all_stops) },
      { label: 'Stops with ETA', value: formatNumber(m.stops_with_eta_count) },
      { label: 'Stops with external ETA', value: formatNumber(m.stops_with_external_eta_count) },
      { label: 'ETA count', value: formatNumber(m.eta_count) },
      { label: 'External ETA count', value: formatNumber(m.external_eta_count) },
    ],
  },
];

const MetricsSection: React.FC<MetricGroup> = ({ title, rows }) => (
  <table className="ui-table compact-table">
    <thead>
      <tr>
        <th colSpan={2}>{title}</th>
      </tr>
    </thead>
    <tbody>
      {rows.map(({ label, value }) => (
        <tr key={label}>
          <td style={{ width: '50%' }}>
            <b>{label}</b>
          </td>
          <td>{value}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const MetricsResult: React.FC<{ metrics: TourAnalyticsMetrics }> = ({ metrics }) => (
  <>
    {buildMetricGroups(metrics).map((group) => (
      <MetricsSection key={group.title} title={group.title} rows={group.rows} />
    ))}
  </>
);

interface PingRowSpec {
  label: string;
  render: (s: PingStats) => React.ReactNode;
}

const buildPingRows = (tzSuffix: string): PingRowSpec[] => [
  { label: 'Total pings', render: (s) => formatNumber(s.totalPings) },
  { label: 'Average ping interval', render: (s) => formatMinutes(s.averageIntervalMinutes) },
  { label: 'Mean ingestion delay', render: (s) => formatDurationSeconds(s.meanIngestionDelaySeconds) },
  { label: 'Mean processing time', render: (s) => formatDurationSeconds(s.meanProcessingTimeSeconds) },
  { label: 'Median processing time', render: (s) => formatDurationSeconds(s.medianProcessingTimeSeconds) },
  { label: `Min ping timestamp ${tzSuffix}`, render: (s) => formatIsoDate(s.minTimestamp) },
  { label: `Max ping timestamp ${tzSuffix}`, render: (s) => formatIsoDate(s.maxTimestamp) },
  { label: `Min received at ${tzSuffix}`, render: (s) => formatIsoDate(s.minReceivedAt) },
  { label: `Max received at ${tzSuffix}`, render: (s) => formatIsoDate(s.maxReceivedAt) },
];

const VehiclePingsSection: React.FC<{ row: VehiclePingsRow }> = ({ row }) => {
  const { timeZone } = useTimeZone();
  const tzSuffix = timeZone === 'UTC' ? '(UTC)' : '(local)';
  const rows = buildPingRows(tzSuffix);
  const title = `Vehicle pings — ${row.licensePlate ?? `#${row.vehicleId}`}`;

  return (
    <table className="ui-table compact-table">
      <thead>
        <tr>
          <th colSpan={3}>{title}</th>
        </tr>
        <tr>
          <th style={{ width: '40%' }} />
          <th>All pings</th>
          <th>Moving pings</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(({ label, render }) => (
          <tr key={label}>
            <td>
              <b>{label}</b>
            </td>
            <td>{render(row.all)}</td>
            <td>{render(row.moving)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const buildVehiclePingsRows = (tour: Tour, data: TourVehicleHistoryQuery | undefined): VehiclePingsRow[] => {
  const histories = data?.tour?.vehicleHistoryList ?? [];
  const breakHistories = data?.tour?.vehicleBreakHistory ?? [];

  return histories.map((entry) => {
    const allocation = tour.vehicleAllocationHistory.find((a) => a.vehicleId === entry.vehicle_id);
    const vehicleTrackingSegments = tour.trackingSegments.filter((s) => s.vehicleId === entry.vehicle_id);
    const vehicleBreaks = breakHistories.find((b) => b.vehicle_id === entry.vehicle_id)?.breaks ?? [];
    const movingPings = filterMovingPings(entry.history, vehicleTrackingSegments, vehicleBreaks);

    return {
      vehicleId: entry.vehicle_id,
      licensePlate: allocation?.vehicleLicensePlateNumber ?? null,
      all: computePingStats(entry.history),
      moving: computePingStats(movingPings),
    };
  });
};

const VehiclePingsResult: React.FC<{ tour: Tour; data: TourVehicleHistoryQuery | undefined }> = ({ tour, data }) => {
  const rows = buildVehiclePingsRows(tour, data);

  if (rows.length === 0) {
    return <Alert kind="info">No vehicle pings recorded for this tour.</Alert>;
  }

  return (
    <>
      {rows.map((row) => (
        <VehiclePingsSection key={row.vehicleId} row={row} />
      ))}
    </>
  );
};

const LoadingState: React.FC<{ label: string }> = ({ label }) => (
  <Stack direction="row" gap="small" alignItems="center">
    <Spinner />
    <span>{label}</span>
  </Stack>
);

interface QueryStateProps {
  loading: boolean;
  error: Error | undefined;
  loadingLabel: string;
  errorLabel: string;
  children: React.ReactNode;
}

const QueryState: React.FC<QueryStateProps> = ({ loading, error, loadingLabel, errorLabel, children }) => {
  if (loading) return <LoadingState label={loadingLabel} />;
  if (error) {
    return (
      <Alert kind="danger">
        {errorLabel}: {error.message}
      </Alert>
    );
  }
  return <>{children}</>;
};

const TRANSPORT_MODE_FILTER: Record<string, TourFilteringTransportMode> = {
  [TransportMode.ROAD]: TourFilteringTransportMode.Road,
  [TransportMode.SEA]: TourFilteringTransportMode.Ocean,
  [TransportMode.AIR]: TourFilteringTransportMode.Air,
};

const getMetricsCompanyId = (tour: Tour): string | null =>
  tour.shipper?.company_id ?? tour.carrier?.company_id ?? tour.createdBy?.company_id ?? null;

const getMetricsTransportMode = (tour: Tour): TourFilteringTransportMode | undefined =>
  tour.transportMode === null ? undefined : TRANSPORT_MODE_FILTER[tour.transportMode];

export const TourAnalyticsTab: React.FC<{ tour: Tour }> = ({ tour }) => {
  const location = useLocation();
  const analyticsPath = Routes.TourAnalytics.generatePath({ tour_id: tour.tour_id.toString() });
  const isActive = location.pathname === analyticsPath;

  const metricsVariables = useMemo<TourAnalyticsMetricsQueryVariables | undefined>(() => {
    const companyId = getMetricsCompanyId(tour);
    if (companyId === null) return undefined;

    const transportMode = getMetricsTransportMode(tour);
    return {
      company_id: companyId,
      tour_id: tour.tour_id.toString(),
      ...(transportMode !== undefined ? { transportMode: [transportMode] } : {}),
    };
  }, [tour]);

  const {
    data: metricsData,
    loading: metricsLoading,
    error: metricsError,
  } = useQuery<TourAnalyticsMetricsQuery, TourAnalyticsMetricsQueryVariables>(tourAnalyticsMetricsQuery, {
    variables: metricsVariables,
    skip: !isActive || metricsVariables === undefined,
    fetchPolicy: 'cache-first',
  });

  const latestMetrics = useMemo<TourAnalyticsMetrics | null>(() => {
    const edges = metricsData?.company?.tourVisibilityPerformance?.tourMetrics.edges ?? [];
    return edges.map((edge) => edge?.node ?? null).find(notNil) ?? null;
  }, [metricsData]);

  const pingsVariables = useMemo<TourVehicleHistoryQueryVariables>(() => {
    const { tour_id, start_time, end_time } = extractVehicleHistoryVariables(tour);
    return {
      tour_id,
      start_time: start_time.toISOString(),
      end_time: end_time.toISOString(),
    };
  }, [tour]);

  const {
    data: pingsData,
    loading: pingsLoading,
    error: pingsError,
  } = useQuery<TourVehicleHistoryQuery, TourVehicleHistoryQueryVariables>(vehicleHistoryQuery, {
    variables: pingsVariables,
    skip: !isActive,
    fetchPolicy: 'cache-first',
  });

  return (
    <Stack direction="column" gap="medium">
      {metricsVariables === undefined ? (
        <Alert kind="info">No company context available to query tour metrics.</Alert>
      ) : (
        <QueryState
          loading={metricsLoading}
          error={metricsError}
          loadingLabel="Loading metrics…"
          errorLabel="Failed to load tour metrics">
          {latestMetrics === null ? (
            <Alert kind="info">No metrics calculated for this tour yet.</Alert>
          ) : (
            <MetricsResult metrics={latestMetrics} />
          )}
        </QueryState>
      )}
      <QueryState
        loading={pingsLoading}
        error={pingsError}
        loadingLabel="Loading vehicle pings…"
        errorLabel="Failed to load vehicle pings">
        <VehiclePingsResult tour={tour} data={pingsData} />
      </QueryState>
    </Stack>
  );
};
